import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import Swal from 'sweetalert2';
import { pdf } from '@react-pdf/renderer';
import { formatoMoneda } from "../../../../configuracion/validaciones";

// Registro de fuentes
Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

// Estilos unificados con ReporteVentas
const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 50,
        paddingHorizontal: 60,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
    },
    companyInfoLeft: {
        flexGrow: 1,
        textAlign: 'left',
    },
    companyName: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 16,
        color: '#333',
        marginBottom: 3,
    },
    companyAddress: {
        fontSize: 9,
        color: '#666',
        marginBottom: 2,
    },
    logoContainer: {
        width: 100,
        height: 100,
        marginLeft: 20,
        marginTop: -10
    },
    logo: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: 15,
        overflow: 'hidden',
    },
    title: {
        fontSize: 16,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#222',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginTop: 15,
    },
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
        marginTop: 5,
    },
    infoColumn: {
        width: '33%',
    },
    infoLabel: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#444',
        marginBottom: 3,
    },
    infoText: {
        fontSize: 10,
        marginBottom: 7,
        color: '#666',
    },
    table: {
        width: '100%',
        marginBottom: 10,
        marginTop: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#F5F5F5',
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 8,
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#333',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#F0F0F0',
        paddingVertical: 6,
    },
    // Ajuste de columnas para Inventario
    colProduct: { width: '35%', paddingLeft: 5 },
    colDetail: { width: '20%', textAlign: 'left' },
    colValue: { width: '10%', textAlign: 'center' },
    colMoney: { width: '15%', textAlign: 'right', paddingRight: 5 },
    itemText: { fontSize: 9, color: '#555' },

    totalsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: 10,
    },
    totalsContainer: {
        width: '45%',
        borderTopWidth: 1.5,
        borderTopColor: '#E0E0E0',
        paddingTop: 10,
    },
    leftColumn: {
        textAlign: 'left',
        width: '50%',
        paddingTop: 5,
    },
    totalsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    totalLabel: { fontSize: 10, color: '#555' },
    totalValue: { fontSize: 10, color: '#333' },

    grandTotalRow: {
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#D0D0D0',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    grandTotalLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 12, color: '#000' },
    grandTotalValue: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 12, color: '#000' },

    amountInWords: {
        fontSize: 8,
        fontFamily: 'Tw_Cen_MT_Negrita',
        textTransform: 'uppercase',
        marginTop: 5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888',
        paddingHorizontal: 60,
    },
    balancePositive: { color: 'green', fontFamily: 'Tw_Cen_MT_Negrita' },
    balanceNegative: { color: 'red', fontFamily: 'Tw_Cen_MT_Negrita' }
});

export const GenerarReporteInventarioPDF = async (inventario, soloProducto = false) => {
    try {
        Swal.fire({
            title: 'Generando Reporte PDF...',
            text: 'Por favor, espere un momento.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const blob = await pdf(<ReporteInventario inventario={inventario} soloProducto={soloProducto} />).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `ReporteInventario_${inventario.id}_${soloProducto ? 'Resumen' : 'Detalle'}.pdf`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        Swal.close();
    } catch (error) {
        Swal.close();
        console.error("Error al generar PDF:", error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el Reporte PDF.' });
    }
};

const ReporteInventario = ({ inventario, soloProducto }) => {
    if (!inventario) return null;

    const { id, fecha, estado, inventarioDetalles = [], Usuario } = inventario;
    const responsableConteo = Usuario?.Empleados?.[0]?.nombreCorto || '—';

    let totalSobrante = 0, totalFaltante = 0, totalCierre = 0;
    let detallesFinales = [];

    // Lógica de procesamiento de datos
    if (soloProducto) {
        const agrupar = inventarioDetalles.reduce((acc, item) => {
            const pId = item.productoId;
            if (!acc[pId]) {
                acc[pId] = { ...item, existenciaSistema: 0, existenciaActual: 0, balance: 0, cierre: 0 };
            }
            const sSist = parseFloat(item.existenciaSistema) || 0;
            const sAct = parseFloat(item.existenciaActual) || 0;
            const sBal = sAct - sSist;
            const sCie = sBal * (parseFloat(item.costo) || 0);

            acc[pId].existenciaSistema += sSist;
            acc[pId].existenciaActual += sAct;
            acc[pId].balance += sBal;
            acc[pId].cierre += sCie;
            return acc;
        }, {});
        detallesFinales = Object.values(agrupar).map(i => ({ ...i, loteNombre: 'CONSOLIDADO' }));
    } else {
        detallesFinales = inventarioDetalles.map(item => {
            const sist = parseFloat(item.existenciaSistema) || 0;
            const act = parseFloat(item.existenciaActual) || 0;
            const bal = act - sist;
            const cie = bal * (parseFloat(item.costo) || 0);
            return { ...item, existenciaSistema: sist, existenciaActual: act, balance: bal, cierre: cie, loteNombre: item.lote?.nombre_lote || '—' };
        });
    }

    detallesFinales.forEach(i => {
        if (i.cierre > 0) totalSobrante += i.cierre;
        else if (i.cierre < 0) totalFaltante += i.cierre;
        totalCierre += i.cierre;
    });

    const textoCierre = NumeroALetras(totalCierre);

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                {/* Encabezado Unificado */}
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn} | Email: {NegocioConfig.correo}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                        <Text style={styles.title}>INVENTARIO FÍSICO N° {id}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}>
                            <Image style={styles.logo} src={NegocioConfig.logo} />
                        </View>
                    )}
                </View>

                {/* Grid de Información */}
                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de Conteo:</Text>
                        <Text style={styles.infoText}>{moment(fecha).format('DD/MM/YYYY HH:mm')}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Responsable:</Text>
                        <Text style={styles.infoText}>{responsableConteo}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Estado:</Text>
                        <Text style={styles.infoText}>{estado}</Text>
                    </View>
                </View>

                {soloProducto && (
                    <Text style={[styles.infoText, { fontFamily: 'Tw_Cen_MT_Negrita', color: '#333' }]}>
                        Reporte resumido por consolidación de productos.
                    </Text>
                )}

                {/* Tabla de Productos */}
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.colProduct}>DESCRIPCIÓN PRODUCTO</Text>
                        {!soloProducto && <Text style={styles.colDetail}>LOTE</Text>}
                        <Text style={styles.colValue}>SIST.</Text>
                        <Text style={styles.colValue}>FÍSICO</Text>
                        <Text style={styles.colValue}>DIF.</Text>
                        <Text style={styles.colMoney}>CIERRE</Text>
                    </View>
                    {detallesFinales.map((item, index) => (
                        <View key={index} style={styles.tableRow}>
                            <Text style={[styles.itemText, styles.colProduct]}>{item.producto?.nombre}</Text>
                            {!soloProducto && <Text style={[styles.itemText, styles.colDetail]}>{item.loteNombre}</Text>}
                            <Text style={[styles.itemText, styles.colValue]}>{item.existenciaSistema}</Text>
                            <Text style={[styles.itemText, styles.colValue]}>{item.existenciaActual}</Text>
                            <Text style={[styles.itemText, styles.colValue, item.balance > 0 ? styles.balancePositive : item.balance < 0 ? styles.balanceNegative : {}]}>
                                {item.balance}
                            </Text>
                            <Text style={[styles.itemText, styles.colMoney]}>{formatoMoneda(item.cierre)}</Text>
                        </View>
                    ))}
                </View>

                {/* Totales y Firmas */}
                <View style={styles.totalsWrapper}>
                    <View style={styles.leftColumn}>
                        <Text style={styles.infoLabel}>VALOR TOTAL EN LETRAS:</Text>
                        <Text style={styles.amountInWords}>{textoCierre}</Text>

                        <View style={{ marginTop: 40, width: '80%', borderTopWidth: 1, borderTopColor: '#333', paddingTop: 5 }}>
                            <Text style={styles.infoLabel}>Firma de Responsable</Text>
                        </View>
                    </View>

                    <View style={styles.totalsContainer}>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalLabel}>Total Sobrante:</Text>
                            <Text style={[styles.totalValue, styles.balancePositive]}>{formatoMoneda(totalSobrante)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalLabel}>Total Faltante:</Text>
                            <Text style={[styles.totalValue, styles.balanceNegative]}>{formatoMoneda(Math.abs(totalFaltante))}</Text>
                        </View>
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>TOTAL CIERRE:</Text>
                            <Text style={styles.grandTotalValue}>{formatoMoneda(totalCierre)}</Text>
                        </View>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text style={{ fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 10, marginBottom: 5 }}>
                        Reporte de Auditoría de Inventarios
                    </Text>
                    <Text>Documento generado internamente para control de existencias.</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ReporteInventario;